/**
 * Static module registry — code-only data, there is no module collection. A tenant's entitlement is
 * persisted as `enabledModules: ModuleAreaKey[]`: registry business keys, never Mongo `_id`s.
 */

export const MODULE_AREA = {
  Fleet: 'fleet',
  Inspections: 'inspections',
  Leasing: 'leasing',
  Maintenance: 'maintenance',
} as const;

export type ModuleAreaKey = (typeof MODULE_AREA)[keyof typeof MODULE_AREA];

/**
 * A UI touch-point a module add-on exposes.
 *
 * `soon` marks a surface that does not exist in the app yet, declared so the discovery page can
 * advertise it. Copy that promises a consequence — the back-office disable dialog naming what will
 * be hidden — MUST filter these out; it may only name surfaces that really exist.
 *
 * `path` is the route the surface lives at. It is what the front office gates on (see
 * {@link moduleAreaForPath}), so this registry is the single list of what belongs to a module:
 * what the Modules tab advertises and what the sidebar and routes hide cannot drift apart. Every
 * BUILT surface must declare one, and a front-office spec checks each against the real route tree.
 * A `soon` surface may declare its future path (a sidebar placeholder) or omit it.
 */
export interface ModuleSurface {
  kind: 'nav' | 'settings' | 'profile-tab';
  labelKey: string;
  path?: string;
  soon?: boolean;
}

/**
 * An add-on inside a module area. Its surfaces drive the disable-dialog copy, which is derived from
 * them rather than hardcoded per module.
 *
 * `sellable: false` is a discovery-only teaser: always "Not included", never written to
 * `enabledModules`, never affects its area's state. Absent or `true` means it follows its area.
 * No add-on currently uses it — the mechanism is kept for the add-ons product has not decided on.
 */
export interface ModuleAddOn {
  descriptionKey: string;
  key: string;
  sellable?: boolean;
  surfaces: ModuleSurface[];
}

/**
 * A module area. `alwaysIncluded` areas are bundled with every tenant and never stored, so "cannot be
 * disabled by any route, including the API" holds by construction — there is no slot to write.
 */
export interface ModuleArea {
  addOns: ModuleAddOn[];
  alwaysIncluded: boolean;
  descriptionKey: string;
  key: ModuleAreaKey;
}

/**
 * Surfaces without `soon` exist today; `soon` ones are advertised but not built. Array order is the order
 * both Modules pages list the areas in (SWOOP-372: Inspections & Compliance sits between Maintenance and
 * Leasing).
 */
export const MODULE_REGISTRY: readonly ModuleArea[] = [
  {
    addOns: [
      {
        descriptionKey: 'modules.addOns.fleetRegister.description',
        key: 'fleet.register',
        surfaces: [
          { kind: 'nav', labelKey: 'modules.surfaces.vehiclesMenu', path: '/vehicles' },
          { kind: 'profile-tab', labelKey: 'modules.surfaces.vehicleProfile', path: '/vehicles/$vehicleId' },
          { kind: 'settings', labelKey: 'modules.surfaces.settingsFleet', path: '/settings/fleet' },
        ],
      },
    ],
    alwaysIncluded: true,
    descriptionKey: 'modules.areas.fleet.description',
    key: MODULE_AREA.Fleet,
  },
  {
    addOns: [
      {
        descriptionKey: 'modules.addOns.maintenanceCore.description',
        key: 'maintenance.core',
        surfaces: [
          { kind: 'settings', labelKey: 'modules.surfaces.maintenanceSettings', path: '/settings/maintenance' },
          {
            kind: 'settings',
            labelKey: 'modules.surfaces.serviceOperations',
            path: '/settings/maintenance/service-operations',
          },
          // Built in SWOOP-281: the Service reminders screen.
          { kind: 'nav', labelKey: 'modules.surfaces.reminders', path: '/reminders' },
          // Built in SWOOP-255: the Issues screen.
          { kind: 'nav', labelKey: 'modules.surfaces.issues', path: '/issues' },
          // Built in SWOOP-325: the Work orders page and its Open-work-order drawer.
          { kind: 'nav', labelKey: 'modules.surfaces.workOrders', path: '/work-orders' },
          // Built in SWOOP-309: the vehicle profile's Maintenance tab.
          {
            kind: 'profile-tab',
            labelKey: 'modules.surfaces.vehicleMaintenance',
            path: '/vehicles/$vehicleId/overview/maintenance',
          },
        ],
      },
    ],
    alwaysIncluded: false,
    descriptionKey: 'modules.areas.maintenance.description',
    key: MODULE_AREA.Maintenance,
  },
  {
    addOns: [
      {
        descriptionKey: 'modules.addOns.inspectionsCore.description',
        key: 'inspections.core',
        // Ordered as the Back Office Modules card lists them, and exactly the three SWOOP-372 names. SWOOP-378
        // built the Inspections menu (`/inspections`) and Settings > Inspections (`/settings/inspections`) as
        // empty pages, both hidden from a tenant without the module. The vehicle profile's Inspections tab
        // remains SOON and has no route yet, so it declares no path. The Open repairs list (SWOOP-376) is not
        // advertised as a separate surface.
        surfaces: [
          { kind: 'nav', labelKey: 'modules.surfaces.inspections', path: '/inspections' },
          { kind: 'settings', labelKey: 'modules.surfaces.inspectionSettings', path: '/settings/inspections' },
          { kind: 'profile-tab', labelKey: 'modules.surfaces.vehicleInspections', soon: true },
        ],
      },
    ],
    alwaysIncluded: false,
    descriptionKey: 'modules.areas.inspections.description',
    key: MODULE_AREA.Inspections,
  },
  {
    addOns: [
      {
        descriptionKey: 'modules.addOns.leasingCore.description',
        key: 'leasing.core',
        // Ordered as the Back Office Modules card lists them. The Leasing menu and Contracts shipped with
        // SWOOP-298; the Leasing tab of Territory Settings with SWOOP-302 (leasing settings are per
        // territory, so it is a territory tab rather than a tenant-wide Settings entry).
        surfaces: [
          { kind: 'nav', labelKey: 'modules.surfaces.leasingMenu', path: '/leasing' },
          { kind: 'nav', labelKey: 'modules.surfaces.contracts', path: '/leasing/contracts' },
          {
            kind: 'settings',
            labelKey: 'modules.surfaces.leasingSettings',
            path: '/settings/territories/$territoryId/leasing',
          },
        ],
      },
    ],
    alwaysIncluded: false,
    descriptionKey: 'modules.areas.leasing.description',
    key: MODULE_AREA.Leasing,
  },
] as const;

/** Every declared surface path, mapped to the area that owns it. Derived once from the registry. */
const SURFACE_PATH_AREAS: ReadonlyMap<string, ModuleAreaKey> = new Map(
  MODULE_REGISTRY.flatMap((area) =>
    area.addOns.flatMap((addOn) =>
      addOn.surfaces.flatMap((surface) => (surface.path === undefined ? [] : [[surface.path, area.key] as const])),
    ),
  ),
);

/**
 * The module area that owns a route, or `undefined` when no module declares it.
 *
 * This is the only way the product decides whether a screen belongs to a module: the front-office
 * sidebar filters on it and route guards check it, so an entry cannot be advertised by the Modules
 * tab yet left visible, or hidden yet never advertised (SWOOP-277).
 */
export function moduleAreaForPath(path: string): ModuleAreaKey | undefined {
  return SURFACE_PATH_AREAS.get(path);
}
